import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { currencyCode, money as fmtAmt } from '../../utils/region';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, useHomeKey, slipAlertHome } from '../../services/popupHome';   // one tab only (report c2846304)

// ── "A client has paid" popup (owner 2026-09-18) ────────────────────────────────────────────────
// A client who accepts a quotation on their no-login link can upload the transfer slip there. Until
// now that landed silently in the database: whoever was chasing the payment only found out if they
// happened to open the right screen. This overlays whatever module the user is on and says so.
//
// Seen is PER USER (quotation_pay_slip_seen), not a shared flag — otherwise the first person to
// glance at it silences the alert for the whole office. Dismissing marks only this user's row, so
// the next colleague still gets told.
//
// It rides the EXISTING 5-minute /api/my-tasks poll rather than adding another one — hence the
// `data` prop: GlobalTasksAlert already holds that response, and a second useMyTasks() here would
// double the traffic for one modal. The email that goes to the office an hour later is a separate
// path (quotationAcceptNotice.ts); this popup is for whoever is at a desk right now.
//
// Who sees it is decided server-side (routes/myTasks.ts): bank people, slip approvers, and whoever
// has the Quotation module. Everyone else gets an empty list and this renders nothing.
//
// Since 2026-10-11 (owner) the same popup announces a LEASE-REGISTRATION slip: the client uploaded
// it on the website's share page and the server mirrored it (source 'lease_reg'). It has no
// quotation to open — doc_no is the registration ("LR-023 · …") and the only place to act on it is
// the Bank Statement of the account it was paid into (scope 'legal' = corporate, 'client' = the
// Dedicated Client Account), where it waits above the table for an approver to pin it to a credit.
// Popup + My Tasks only — no e-mail (owner's decision).

export default function SlipAlert({ data }: { data: any }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const alerts: any[] = (data?.slipAlerts || []).filter((s: any) => !dismissed.has(s.id));
  const top = alerts[0];

  // Esc dismisses, same as the button — a modal that traps the keyboard is worse than no modal.
  useEffect(() => {
    if (!top) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') void dismissAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // no dep array: `top` changes identity each poll and the handler closes over it

  const dismissAll = async () => {
    const ids = alerts.map(a => a.id);
    if (!ids.length) return;
    setDismissed(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
    try { await api.slipSeen(ids); } catch { /* it will simply show again at the next poll */ }
  };

  // Report c2846304: the pop-up is put up on the page its own button opens — the bank page of the account a
  // lease-registration slip was paid into, the quotation otherwise — and in that one tab only. With that page
  // open in no tab the tab in front offers to open it (PopupHomeNotice); and since most of this pop-up's
  // audience cannot open the bank pages, it is shown where they are when that is so (services/popupHome).
  // Held still while the pop-up is up (useHomeKey): the page is read off the FIRST waiting slip, and a
  // poll that handed them back in another order moved this pop-up between the dialog and the little card
  // on its own (opening #2 of the same report).
  const homePage = useHomeKey(slipAlertHome(top), !!top);
  const home = usePopupHome(homePage, !!top);
  if (!top) return null;
  if (home !== 'show') return home === 'notice' ? <PopupHomeNotice page={homePage} /> : null;
  const more = alerts.length - 1;
  const isLease = top.source === 'lease_reg';
  // Which slot the slip fills follows the account it was paid into: the Dedicated Client Account
  // takes the land-office money, the corporate account the legal fee. Labels as the website prints them.
  const leaseSlot = top.scope === 'client' ? '[LAND OFFICE FEES/TAXES]' : '[LEGAL FEES]';
  const leaseBankPath = top.scope === 'client' ? '/m/bank_client' : '/m/bank_legal';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', zIndex: 13000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 'min(460px, 100%)', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: '0 22px 70px #0007', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 18px', background: '#E6F6EF', borderBottom: '1px solid #B9E5D2' }}>
          <i className="ti ti-cash-banknote" style={{ fontSize: 22, color: '#0F6B49' }} />
          <div style={{ fontSize: 15.5, fontWeight: 800, color: '#0F6B49' }}>{t('slip_alert_title')}</div>
        </div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <b>{top.client_name || '—'}</b> {isLease ? t('slip_alert_lease_line', { slot: leaseSlot }) : t('slip_alert_line')} <b>{top.doc_no}</b>
            {Number(top.amount) > 0 && <> · <b>{currencyCode()} {fmtAmt(top.amount)}</b></>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', background: '#FEF6E0', border: '1px solid #F0DCA6', borderRadius: 8, padding: '9px 12px' }}>
            <i className="ti ti-alert-triangle" style={{ fontSize: 13, color: '#8A6212', marginRight: 6, verticalAlign: '-2px' }} />
            {isLease ? t('slip_alert_lease_tbc') : t('slip_alert_tbc')}
          </div>
          {more > 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>+{more}</div>}
          {/* One button only. The Bank Statement lives at a per-company route (/m/bank_acc,
              /m/bank_legal) that most of this popup's audience cannot open — a second button would
              dead-end them on a permission page. The quotation is where everyone can look.
              A lease-registration slip has no quotation: its one button goes to the bank page of the
              account it was paid into, which is the only place it can be confirmed. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {isLease ? (
              <button onClick={() => { void dismissAll(); window.open(leaseBankPath, '_blank'); }}
                style={{ flex: 1, border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', background: 'var(--accent)' }}>
                <i className="ti ti-building-bank" style={{ fontSize: 13, marginRight: 5, verticalAlign: '-2px' }} />{t('slip_alert_lease_open')}
              </button>
            ) : (
              <button onClick={() => { void dismissAll(); window.open(`/m/peak?tab=quotation&open=${encodeURIComponent(top.quotation_id)}`, '_blank'); }}
                style={{ flex: 1, border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', background: 'var(--accent)' }}>
                <i className="ti ti-file-text" style={{ fontSize: 13, marginRight: 5, verticalAlign: '-2px' }} />{t('slip_alert_open_quote')}
              </button>
            )}
          </div>
          <button onClick={dismissAll}
            style={{ alignSelf: 'center', marginTop: 2, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
            {t('slip_alert_dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
